/**
 * Feld zum Unterschreiben mit dem Finger.
 *
 * Eine Leinwand, auf der mit Zeigergesten gezeichnet wird – Finger,
 * Stift und Maus gleichermaßen, über Pointer-Ereignisse. Heraus kommt ein
 * PNG mit durchsichtigem Hintergrund, damit die Unterschrift im Bericht
 * auf dem Papier steht und nicht in einem weißen Kasten.
 *
 * Zwei Dinge, die man leicht falsch macht:
 *
 *  - Die Leinwand braucht eine eigene Pixelgröße, unabhängig von ihrer
 *    Darstellungsgröße. Ohne das ist der Strich auf einem Handy mit
 *    dreifacher Auflösung ein grober Klotz.
 *  - `touch-action: none`. Sonst scrollt die Seite unter dem Finger weg,
 *    statt dass ein Strich entsteht.
 */

import { useEffect, useRef, useState } from 'react'

// Die feste Zeichenfläche in Bildpunkten. Seitenverhältnis wie ein
// Unterschriftenfeld auf Papier, breit und flach.
const BREITE = 1000
const HOEHE = 300

export function Unterschriftfeld({
  vorhanden,
  beimZeichnen,
}: {
  /** Eine schon hinterlegte Unterschrift, die zunächst angezeigt wird. */
  vorhanden?: string | null
  /** Meldet den aktuellen Stand: PNG-Datenurl oder null, wenn leer. */
  beimZeichnen: (bild: string | null) => void
}) {
  const leinwand = useRef<HTMLCanvasElement>(null)
  const zeichnet = useRef(false)
  const [leer, setzeLeer] = useState(!vorhanden)

  // Eine bereits hinterlegte Unterschrift zu Beginn hineinmalen, damit man
  // sieht, was man ersetzt.
  useEffect(() => {
    const flaeche = leinwand.current
    if (!flaeche || !vorhanden) return
    const stift = flaeche.getContext('2d')
    if (!stift) return

    const bild = new Image()
    bild.onload = () => stift.drawImage(bild, 0, 0, BREITE, HOEHE)
    bild.src = vorhanden
  }, [vorhanden])

  function punkt(e: React.PointerEvent<HTMLCanvasElement>) {
    const flaeche = leinwand.current!
    const rahmen = flaeche.getBoundingClientRect()
    // Von der Darstellungsgröße auf die Zeichenfläche umrechnen.
    return {
      x: ((e.clientX - rahmen.left) / rahmen.width) * BREITE,
      y: ((e.clientY - rahmen.top) / rahmen.height) * HOEHE,
    }
  }

  function stiftHolen() {
    const stift = leinwand.current?.getContext('2d')
    if (!stift) return null
    stift.lineWidth = 4
    stift.lineCap = 'round'
    stift.lineJoin = 'round'
    stift.strokeStyle = '#111827'
    return stift
  }

  function beginnen(e: React.PointerEvent<HTMLCanvasElement>) {
    const stift = stiftHolen()
    if (!stift) return
    // Den Zeiger festhalten: sonst reißt der Strich ab, sobald der Finger
    // über den Rand der Leinwand gerät.
    e.currentTarget.setPointerCapture(e.pointerId)
    zeichnet.current = true
    const p = punkt(e)
    stift.beginPath()
    stift.moveTo(p.x, p.y)
  }

  function ziehen(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!zeichnet.current) return
    const stift = stiftHolen()
    if (!stift) return
    const p = punkt(e)
    stift.lineTo(p.x, p.y)
    stift.stroke()
    if (leer) setzeLeer(false)
  }

  /* Kein `onPointerLeave`: der Zeiger ist festgehalten, also kommen die
     Ereignisse weiter, auch wenn der Finger über den Rand gerät. Ein
     Beenden beim Verlassen risse den Strich genau dort ab, wo man am Rand
     des Feldes unterschreibt – und das tut fast jeder. */
  function beenden() {
    if (!zeichnet.current) return
    zeichnet.current = false
    const flaeche = leinwand.current
    if (!flaeche) return
    beimZeichnen(flaeche.toDataURL('image/png'))
  }

  function leeren() {
    const stift = leinwand.current?.getContext('2d')
    stift?.clearRect(0, 0, BREITE, HOEHE)
    setzeLeer(true)
    beimZeichnen(null)
  }

  return (
    <div>
      <canvas
        ref={leinwand}
        width={BREITE}
        height={HOEHE}
        onPointerDown={beginnen}
        onPointerMove={ziehen}
        onPointerUp={beenden}
        onPointerCancel={beenden}
        className="w-full rounded-feld border-2 border-dashed border-slate-400 bg-white"
        style={{ touchAction: 'none', aspectRatio: `${BREITE} / ${HOEHE}` }}
        aria-label="Unterschriftfeld"
      />
      <div className="mt-2 flex items-center gap-3">
        <button type="button" className="knopf-still px-3 text-base" onClick={leeren}>
          Löschen
        </button>
        <span className="text-base text-slate-600">
          {leer ? 'Mit dem Finger unterschreiben.' : 'Unterschrift erfasst.'}
        </span>
      </div>
    </div>
  )
}
